import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Input, Badge, Button } from '../../components/ui';
import { trackEvent } from '../../services/tracking';
import { Search, FileText, Clock, CheckCircle, AlertCircle, Plus, X } from 'lucide-react';
import { programsAPI, type Program } from '../../lib/services';
import { submissionService, type Submission } from '../../services/submissions';

const VendorSubmissions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [availablePrograms, setAvailablePrograms] = useState<Program[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check for success message from navigation state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Fetch available programs and submissions on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingPrograms(true);
        setLoadingSubmissions(true);
        
        // Fetch both in parallel
        const [programs, subs] = await Promise.all([
          programsAPI.list({ status: 'active' }),
          submissionService.list()
        ]);
        
        setAvailablePrograms(programs);
        setSubmissions(subs);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoadingPrograms(false);
        setLoadingSubmissions(false);
      }
    };

    fetchData();
  }, []);

  const filteredSubmissions = submissions.filter((sub) =>
    sub.program_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return { variant: 'pending' as const, label: 'Draft', icon: FileText };
      case 'submitted':
        return { variant: 'info' as const, label: 'Submitted', icon: CheckCircle };
      case 'under_review':
        return { variant: 'in-progress' as const, label: 'Under Review', icon: Clock };
      case 'scored':
        return { variant: 'in-progress' as const, label: 'Scored', icon: Clock };
      case 'selected':
        return { variant: 'approved' as const, label: 'Selected', icon: CheckCircle };
      case 'rejected':
        return { variant: 'rejected' as const, label: 'Not Selected', icon: AlertCircle };
      default:
        return { variant: 'pending' as const, label: status, icon: FileText };
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground">Your Submissions</h1>
            <p className="text-base text-muted-foreground mt-2">
              Manage your RFP submissions and track their status
            </p>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-8 bg-success/10 border border-success/30 rounded-lg p-4 flex items-start justify-between">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-success mt-0.5" />
              <p className="text-sm text-green-900 dark:text-green-100">{successMessage}</p>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-success hover:text-green-800 dark:hover:text-green-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Submissions</p>
                <p className="text-3xl font-bold text-foreground mt-1">
                  {loadingSubmissions ? '...' : submissions.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Under Review</p>
                <p className="text-3xl font-bold text-foreground mt-1">
                  {loadingSubmissions ? '...' : submissions.filter((s) => s.status === 'under_review' || s.status === 'scored').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-primary" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Needs Action</p>
                <p className="text-3xl font-bold text-foreground mt-1">
                  {loadingSubmissions ? '...' : submissions.filter((s) => s.status === 'draft').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/20 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-warning" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Selected</p>
                <p className="text-3xl font-bold text-foreground mt-1">
                  {loadingSubmissions ? '...' : submissions.filter((s) => s.status === 'selected').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
            </div>
          </Card>
        </div>

        {/* Available Programs to Submit */}
        {loadingPrograms ? (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Open RFPs
            </h2>
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        ) : availablePrograms.length > 0 ? (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Open RFPs
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {availablePrograms.map((program) => (
                <Card
                  key={program.id}
                  className="p-6 cursor-pointer hover:shadow-lg transition-shadow hover:border-blue-500 dark:hover:border-blue-500"
                  onClick={() => { trackEvent('rfp_program_viewed', { program_id: program.id, program_name: program.name }); navigate(`/submit/new/${program.id}`) }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {program.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Status: {program.status_display}
                      </p>
                      {program.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {program.description}
                        </p>
                      )}
                    </div>
                    <Button variant="default" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Submit
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ) : null}

        {/* Search */}
        <div className="bg-white dark:bg-slate-900 border border-border rounded-lg p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search submissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9"
            />
          </div>
        </div>

        {/* Submissions List */}
        {loadingSubmissions ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSubmissions.map((submission) => {
              const statusInfo = getStatusBadge(submission.status);
              const StatusIcon = statusInfo.icon;

              return (
                <Card
                  key={submission.id}
                  className="p-6 cursor-pointer hover:shadow-lg transition-shadow hover:border-blue-500 dark:hover:border-blue-500"
                  onClick={() => { trackEvent('submission_viewed', { submission_id: submission.id, program_name: submission.program_name, status: submission.status }); navigate(`/submit/${submission.id}`) }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">
                          {submission.program_name}
                        </h3>
                        <Badge variant={statusInfo.variant} className="flex items-center gap-1">
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        {submission.submission_date && (
                          <span>Submitted: {formatDate(submission.submission_date)}</span>
                        )}
                        <span>Last Updated: {formatDate(submission.last_updated)}</span>
                        {submission.documents_count !== undefined && (
                          <span>{submission.documents_count} files</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}

            {filteredSubmissions.length === 0 && !loadingSubmissions && (
              <Card className="p-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  {searchQuery ? 'No submissions match your search.' : 'No submissions yet.'}
                </p>
                {availablePrograms.length > 0 && !searchQuery && (
                  <Button variant="default" onClick={() => navigate(`/submit/new/${availablePrograms[0].id}`)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Submission
                  </Button>
                )}
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorSubmissions;

